import { useMemo } from "preact/hooks"
import type { Page } from "@nosto/search-js/preact/hooks"
import { pageSliceWithInGrid, totalPagesWithInGrid } from "@/utils/paginationWithInGrid"

function range(from: number, toExclusive: number): number[] {
  const n = toExclusive - from
  if (!Number.isFinite(n) || n <= 0) {
    return []
  }
  return Array.from({ length: n }, (_, i) => from + i)
}

/**
 * Mirrors {@link usePagination} from `@nosto/search-js`, but uses deduped totals
 * and the current display page instead of Nosto raw offsets. When the first
 * page reserves `inGridSlots` cells for in-grid tiles, totals and ranges are
 * adjusted so the displayed counts match the actually-visible products.
 */
export function useDedupedPagination(options: {
  width?: number
  dedupedTotal: number
  pageSize: number
  currentPage: number
  inGridSlots?: number
}): {
  totalPages: number
  resultsFrom: number
  resultsTo: number
  current?: Page
  prev?: Page
  next?: Page
  first?: Page
  last?: Page
  pages: Page[]
} {
  const { width = 5, dedupedTotal, pageSize, currentPage, inGridSlots = 0 } = options

  return useMemo(() => {
    if (dedupedTotal <= 0 || pageSize <= 0) {
      return { totalPages: 0, resultsFrom: 0, resultsTo: 0, pages: [] }
    }

    const totalPages = totalPagesWithInGrid(dedupedTotal, pageSize, inGridSlots)
    const halfWindow = Math.max(Math.floor((width - 1) / 2), 1)
    const o = Math.min(Math.max(1, currentPage), totalPages)

    const pageToEntry = (pageNum: number): Page => {
      const { start } = pageSliceWithInGrid(pageNum, pageSize, inGridSlots)
      return { from: start, page: pageNum, current: pageNum === o }
    }

    const current = pageToEntry(o)
    const prev = o > 1 ? pageToEntry(o - 1) : undefined
    const next = o < totalPages ? pageToEntry(o + 1) : undefined
    const first = o - halfWindow - 1 > 1 ? pageToEntry(1) : undefined
    const last = o + halfWindow + 1 < totalPages ? pageToEntry(totalPages) : undefined

    const inWindow = (p: number) => p >= o - halfWindow && p <= o + halfWindow
    let pages = range(1, totalPages + 1)
      .filter(inWindow)
      .map(pageToEntry)
    if (!first && pages[0]?.page === 2) {
      pages = [pageToEntry(1), ...pages]
    }
    if (!last && pages[pages.length - 1]?.page === totalPages - 1) {
      pages = [...pages, pageToEntry(totalPages)]
    }

    const { start, end } = pageSliceWithInGrid(o, pageSize, inGridSlots)
    const safeEnd = Math.min(end, dedupedTotal)
    const resultsFrom = safeEnd > start ? start + 1 : 0
    const resultsTo = safeEnd

    return {
      totalPages,
      resultsFrom,
      resultsTo,
      current,
      prev,
      next,
      first,
      last,
      pages
    }
  }, [width, dedupedTotal, pageSize, currentPage, inGridSlots])
}
